import React, { JSX, Suspense, useMemo } from "react";

import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCountLastHourQuery } from "src/api/locationChartQueries.api";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import LiveSection from "src/components/LiveSection/LiveSection";
import { dashboardChartTooltipFormatter } from "src/components/Tooltip/helpers/dashboardChartTooltipFormatter";
import ComponentId from "src/constants/componentId.enum";
import Granularity from "src/constants/granularity.enum";
import IntervalType from "src/constants/intervalType.enum";
import SeriesType from "src/constants/seriesType.enum";
import sharedStyles from "src/constants/styles";
import { mapSiteOccupancyLastHour } from "src/lib/chart.helper";
import { formatNum, getSumByTime, getTotalInOutValues } from "src/lib/number.helpers";

import { ChartTooltipFormatterParams, type DashboardComponentParams } from "../interfaces/DashboardComponentParams";

const ReactECharts = React.lazy(() => import("echarts-for-react"));
const styles = {
  chartContainer: { height: 300 },
};

function OccupancyLastHourComponent({ selectedLocation }: DashboardComponentParams): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data } = useCountLastHourQuery(selectedLocation.Id, Granularity.FiveMinutes, null);

  const chartData = useMemo(() => data?.value ?? [], [data?.value]);
  const totalInOutValues = useMemo(() => getTotalInOutValues(chartData), [chartData]);
  const sumByTime: Record<string, number[]> = useMemo(() => getSumByTime(chartData), [chartData]);

  const option = useMemo(
    () => ({
      tooltip: {
        confine: true,
        trigger: "axis",
        formatter: (params: ChartTooltipFormatterParams) => dashboardChartTooltipFormatter(params, IntervalType.Hour, t, theme),
      },
      legend: {
        left: "left",
        icon: "square",
        top: 0,
        formatter: (name: string) => {
          const valueMap: Record<string, number> = {
            [t("Entering")]: totalInOutValues[0],
            [t("Leaving")]: totalInOutValues[1],
          };
          return `${formatNum(valueMap[name] ?? 0)} ${name}`;
        },
        textStyle: {
          color: theme.palette.secondary.light,
          padding: -5,
          fontWeight: theme.typography.fontWeightRegular,
          fontSize: theme.fontSizes.md,
        },
      },
      grid: {
        left: "0%",
        right: "0%",
        bottom: "12%",
        top: "10%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          name: t("Now"),
          nameLocation: "end",
          nameGap: 0,
          nameTextStyle: {
            align: "right",
            verticalAlign: "top",
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.typography.fontWeightLight,
            color: theme.palette.primary.main,
            padding: [20, 0, 0, 0],
          },
          axisLabel: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: t("1 hour ago"),
          nameLocation: "start",
          nameGap: 0,
          nameTextStyle: {
            align: "left",
            verticalAlign: "top",
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.typography.fontWeightLight,
            padding: [20, 0, 0, 0],
          },
          axisLabel: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: t("Entering"),
          type: SeriesType.Bar,
          stack: "total",
          emphasis: {
            focus: "series",
          },
          data: Object.keys(sumByTime)
            .sort()
            .map((time) => [time, sumByTime[time][0]]),
          itemStyle: {
            color: theme.palette.primary.main,
          },
        },
        {
          name: t("Leaving"),
          type: SeriesType.Bar,
          stack: "total",
          label: {
            show: false,
          },
          emphasis: {
            focus: "series",
          },
          data: Object.keys(sumByTime)
            .sort()
            .map((time) => [time, sumByTime[time][1]]),
          itemStyle: {
            color: theme.palette.primary.dark,
          },
        },
      ],
    }),
    [totalInOutValues, sumByTime, t, theme]
  );

  const dataRows = useMemo(
    () => (data ? mapSiteOccupancyLastHour(data, selectedLocation.LocalName) : []),
    [data, selectedLocation.LocalName]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <Box mb={5} sx={sharedStyles(theme).spaceBetweenFlexbox}>
        <Typography
          color={theme.palette.text.secondary}
          fontSize={theme.fontSizes.sm}
          fontWeight={theme.typography.fontWeightRegular}
          textTransform='uppercase'
        >
          {t("Occupancy last hour")}
        </Typography>
        <Box display='flex' alignItems='center' mt={-1} gap={1}>
          <LiveSection />
          <DownloadCSV componentId={ComponentId.OccupancyLastHour} locationName={selectedLocation.LocalName} rows={dataRows} />
        </Box>
      </Box>
      <Box sx={styles.chartContainer}>
        <Suspense fallback={<ChartSkeleton />}>
          {data ? <ReactECharts option={option} /> : <ChartSkeleton />}
        </Suspense>
      </Box>
    </Paper>
  );
}

export default OccupancyLastHourComponent;
